/**
 * Shared helpers for the "Request Parameters" UX used by both V2 Domestic
 * (TypeOfServiceStep, LegalOrderDetailsStep) and V2 International
 * (InternationalTypeOfServiceStep, InternationalLegalOrderDetailsStep).
 *
 * - parseIdentifierValues: splits a comma- or newline-separated raw string into
 *   trimmed, non-empty value tokens.
 * - buildIdentifierKey: produces a normalized lookup key for dedup-by-type-and-value.
 * - validateDateRange: enforces the historical-data range invariants. When
 *   `allowsOverride` is true (Preservation / Lawful Intercept-style flows), the
 *   "end date cannot be in the future" check is skipped.
 * - getTodayLocalDateString: returns today's date as YYYY-MM-DD in the user's
 *   LOCAL time zone. Use this for the `max` attribute on `<input type="date">`
 *   inputs - HTML date inputs interpret their value in local time, so a UTC
 *   string produces off-by-one-day caps for users in non-zero time zones.
 * - allowsDateOverride: returns true if the given request type is a
 *   forward-looking surveillance order (PR/TT or Lawful Intercept) that
 *   permits future-dated end dates via the Date Range Override card.
 * - getEffectiveDateRange: returns the date range that will actually be sent
 *   to the backend (override range when applicable, regular range otherwise).
 *   Used by both the submission payload builder and the Review step so the
 *   dates the user attests match the dates that get submitted.
 */

import { z } from 'zod';
import { firstError } from '@/utils/formSchemas';
import { REQUEST_TYPES_WITH_DATE_OVERRIDE } from '../constants/requestTypes';

export const parseIdentifierValues = (raw: string): string[] => {
    return raw
        .split(/[\n,]/)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
};

export const buildIdentifierKey = (typeValue: string, normalizedValue: string): string => {
    return [typeValue.trim().toLowerCase(), normalizedValue.trim().toLowerCase()].join('|');
};

export const getTodayLocalDateString = (): string => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

export const allowsDateOverride = (requestType: string | null): boolean => {
    if (!requestType) return false;
    return REQUEST_TYPES_WITH_DATE_OVERRIDE.includes(requestType);
};

const START_DATE_MESSAGE =
    'Start date must be between January 1, 1990 and today, and must not be after the end date';
const END_DATE_MESSAGE =
    'End date must be between January 1, 1990 and today, and must not be before the start date';
const OVERRIDE_END_DATE_MESSAGE =
    'End date must be on or after January 1, 1990 and must not be before the start date';

/**
 * A strict YYYY-MM-DD local date. Malformed strings and impossible days (e.g. Feb 30, which
 * `new Date` would silently roll over into March) fail, so no Invalid Date reaches the range
 * checks, where comparisons with it are always false.
 */
const ymdDateSchema = z.string().transform((value, ctx) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const isExactDay =
        !Number.isNaN(date.getTime()) &&
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;
    if (!isExactDay) {
        ctx.addIssue({ code: 'custom', message: 'Invalid date' });
        return z.NEVER;
    }
    return date;
});

const dateRangeSchema = (allowsOverride: boolean) =>
    z
        .object({ start: ymdDateSchema, end: ymdDateSchema })
        .superRefine(({ start, end }, ctx) => {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            const minDate = new Date(1990, 0, 1);

            // Consolidated Start date rule (PM-approved wording 2026-06-04).
            // Covers pre-1990, future, and after-end in one actionable sentence.
            if (start < minDate || start > today || start > end) {
                ctx.addIssue({ code: 'custom', path: ['start'], message: START_DATE_MESSAGE });
                return;
            }

            // Consolidated End date rule, parallel to Start. Override request types
            // (Preservation / PR/TT / Lawful Intercept) drop only the future-end
            // constraint - the 1990 floor and the on/after-start ordering still apply.
            const endIsFutureAndNotAllowed = !allowsOverride && end > today;
            if (end < minDate || end < start || endIsFutureAndNotAllowed) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['end'],
                    message: allowsOverride ? OVERRIDE_END_DATE_MESSAGE : END_DATE_MESSAGE,
                });
            }
        });

export const validateDateRange = (
    startDate: string | null,
    endDate: string | null,
    allowsOverride = false,
): string | null => {
    if (!startDate || !endDate) {
        return null;
    }

    const result = dateRangeSchema(allowsOverride).safeParse({ start: startDate, end: endDate });
    return result.success ? null : firstError(result.error);
};

export interface DateRangePair {
    startDate: string | null;
    endDate: string | null;
}

export interface EffectiveDateRangeInput {
    requestType: string | null;
    regularStartDate: string | null;
    regularEndDate: string | null;
    overrideStartDate: string | null;
    overrideEndDate: string | null;
}

/**
 * Picks the date range that should actually be sent to the backend
 * (and shown on the Review screen). Override range wins when the request
 * type allows override AND both override fields are set; otherwise the
 * regular range is used. Single source of truth for both the submission
 * payload builder and the Review display so the user reviews what they
 * actually submit.
 */
export const getEffectiveDateRange = (input: EffectiveDateRangeInput): DateRangePair => {
    const overrideActive = allowsDateOverride(input.requestType)
        && !!input.overrideStartDate
        && !!input.overrideEndDate;

    return overrideActive
        ? { startDate: input.overrideStartDate, endDate: input.overrideEndDate }
        : { startDate: input.regularStartDate, endDate: input.regularEndDate };
};
